// ============================================================================
// LEDGER POSTING HOOKS (Fairbooks posting register)
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { ledgerPostingService } from 'api/services/ledgerPostingService';
import type { LedgerPosting, LedgerPostingSearchRequest, ID } from 'api/types';

export const useLedgerPostings = (params?: LedgerPostingSearchRequest) => {
  const [data, setData] = useState<LedgerPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 25,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ledgerPostingService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { data, loading, error, pagination, refetch: fetchAll };
};

export const useRetryLedgerPosting = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const retryLedgerPosting = async (
    id: ID,
    options?: { onSuccess?: (item: LedgerPosting) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await ledgerPostingService.retry(id);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { retryLedgerPosting, loading, error };
};
